import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { toast } from "../../utils/toast";
import { errorMessageOf } from "../../api/scholarships";
import { Table, Tag } from "../ui";
import { BURSARIES_KEY, nameOf, useDebouncedSearch } from "./shared";

const PAGE_SIZE = 100;

// Lower S6 aggregates are better: 15 and under is strong, over 20 is weak
const aggregateColor = (aggregate) => {
  const value = parseInt(aggregate, 10);
  if (Number.isNaN(value)) return "gray";
  if (value <= 15) return "green";
  return value <= 20 ? "yellow" : "red";
};

const COLUMNS = [
  { key: "index", label: "#", className: "text-slate-500" },
  { key: "registrationNumber", label: "Reg number", className: "whitespace-nowrap font-semibold text-slate-900" },
  { key: "name", label: "Student", accessor: nameOf },
  { key: "gender", label: "Gender" },
  { key: "indexNumber", label: "Index number" },
  {
    key: "s6Aggregate",
    label: "S6 aggregate",
    render: (s) => (s.s6Aggregate ? <Tag color={aggregateColor(s.s6Aggregate)}>{s.s6Aggregate}</Tag> : "—"),
  },
  { key: "college", label: "College", accessor: (s) => s.college?.name ?? "—" },
  { key: "qualification", label: "Qualification", accessor: (s) => s.qualification?.name ?? "—" },
  { key: "studyArea", label: "Study area", accessor: (s) => s.qualification?.studyArea ?? "—" },
  { key: "educationLevel", label: "Education level" },
  { key: "claimed", label: "Account", accessor: (s) => (s.claimed ? "Claimed" : "Not claimed") },
];

/** An institution's admitted students for a year, one page at a time from `list(params)`, searched on the server. */
export default function AdmittedStudentsTable({ list, params, exportName }) {
  const [page, setPage] = useState(0);
  const { search, setSearch, debounced } = useDebouncedSearch(() => setPage(0));

  const { data, isPending } = useQuery({
    queryKey: [BURSARIES_KEY, "students", params, page, debounced],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      try {
        return (await list({ ...params, page, size: PAGE_SIZE, ...(debounced && { q: debounced }) })).data;
      } catch (error) {
        console.error("Failed to fetch admitted students:", error);
        toast.error(errorMessageOf(error, "Failed to fetch the admitted students."), { id: "admitted-students-error" });
        throw error;
      }
    },
  });

  // Numbered across pages, not per page
  const rows = (data?.items ?? []).map((s, i) => ({ ...s, index: page * PAGE_SIZE + i + 1 }));
  const totalElements = data?.total ?? rows.length;

  return (
    <Table
      columns={COLUMNS}
      data={rows}
      isLoading={isPending}
      searchPlaceholder="Search by reg number, name, national ID, index number, email or phone"
      searchValue={search}
      onSearchChange={setSearch}
      exportName={exportName}
      emptyTitle={debounced ? "No students match your search" : "No admitted students uploaded"}
      pagination={{ page, totalPages: Math.ceil(totalElements / PAGE_SIZE), totalElements, pageSize: PAGE_SIZE, onPageChange: setPage }}
    />
  );
}
